import React, { ReactElement } from 'react';
import { alpha, Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { Card } from 'modules/common';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';
import { IotaTokenIcon } from 'modules/icons';

import { translation } from './translation';

const mergedTranslation = mergeTranslations(globalTranslation, translation);

export function Booster(): ReactElement {
  const { classes } = useStyles();
  const { t, keys } = useTranslation(mergedTranslation);

  const { onOpen } = useDialog(KnownDialogs.boostRewards);

  const APY = 4.99;
  const VALUE = 0.0;
  const VALUE_USD = 0.0;

  return (
    <Card>
      <div className={classes.root}>
        <div className={classes.apy}>{t(keys.apy, { value: APY })}</div>

        <div className={classes.title}>{t(keys.boosterRewards)}</div>

        <div className={classes.valueWrap}>
          <IotaTokenIcon className={classes.unit} />

          <div>
            <div className={classes.value}>{VALUE}</div>

            <div className={classes.valueUsd}>
              {t(keys.unit.$, { value: VALUE_USD })}
            </div>
          </div>
        </div>

        <div className={classes.actions}>
          <Button
            className={classes.action}
            variant="outlined"
            onClick={onOpen}
          >
            {t(keys.stakeFor, { stakedToken: t(keys.tokens.native) })}
          </Button>

          <Button className={classes.action} variant="outlined">
            {t(keys.claim)}
          </Button>
        </div>
      </div>
    </Card>
  );
}

const useStyles = makeStyles()(theme => ({
  root: {
    position: 'relative',
  },
  apy: {
    position: 'absolute',
    right: 0,
    fontSize: 14,
    borderRadius: 8,
    background: alpha(theme.palette.secondary.contrastText, 0.05),
    padding: theme.spacing(1.4, 2.25),
    color: alpha(theme.palette.secondary.contrastText, 0.5),

    '&:before': {
      display: 'block',
      content: '""',
      position: 'absolute',
      inset: '0',
      borderRadius: 8,
      padding: theme.spacing(0.25),
      background: `linear-gradient(96.09deg, ${alpha(theme.palette.secondary.contrastText, 0.1)} 0%, ${alpha(theme.palette.secondary.contrastText, 0)} 100%), linear-gradient(96.09deg, ${alpha(theme.palette.common.black, 0)} 0%, ${alpha(theme.palette.common.black, 0.1)} 100%)`,
      mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
      maskComposite: 'exclude',
    },
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: alpha(theme.palette.secondary.contrastText, 0.5),
    paddingRight: theme.spacing(25),
  },
  valueWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    margin: theme.spacing(7, 0, 10),

    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(4, 0, 4.5),
    },
  },
  unit: {
    width: 32,
  },
  value: {
    fontWeight: 700,
    fontSize: 16,
    color: theme.palette.secondary.contrastText,
  },
  valueUsd: {
    fontWeight: 500,
    fontSize: 12,
    marginTop: theme.spacing(1),
    color: alpha(theme.palette.secondary.contrastText, 0.5),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(3),
  },
  action: {
    flex: 1,
    fontWeight: 400,
    fontSize: 12,
  },
}));
